import { AssetDropdownInput } from "#src/batteries-included-components/Dropdowns";
import { EntityDropdownInput } from "#src/batteries-included-components/Dropdowns/EntityDropdownInput";
import { useGetAssetTypeOptions } from "#src/batteries-included-components/FilterAreas/assetFilters.helpers";
import { useListCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import { useListEquipmentTypes } from "#src/components/hooks/adapters/useEquipment";
import {
  useListFlowProductCategories,
  useListFlowProductTypes,
  useListFlowTypes,
} from "#src/components/hooks/adapters/useFlows";
import { useListMeasurementTypes } from "#src/components/hooks/adapters/useMeasurementTypes";
import { useUpdateWorkflowTemplateByApiVersion } from "#src/components/hooks/adapters/useWorkflowTemplates";
import { useListDeviceTypes } from "#src/components/hooks/adapters/utils/useDevices";
import {
  MultiStepFormProvider,
  useMemoizedFormState,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import {
  Dialog,
  DialogProps,
  FilterBuilder,
  FilterBuilderFieldDefinition,
  FilterBuilderFieldsType,
  FilterBuilderSupportedDataTypesType,
  Form,
  ListInput,
  RadioInput,
  TextAreaInput,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  AttributeDataType,
  AttributeDataTypeType,
  CustomAttributeType,
  DeviceStatus,
  EquipmentStatus,
  FacilityStatus,
  FlowStatus,
  UsersAdapter,
  WorkflowCategoriesAdapter,
  WorkflowTemplateType,
} from "@validereinc/domain";
import { Assets } from "@validereinc/domain-controllers";
import classNames from "classnames/bind";
import { startCase } from "es-toolkit/compat";
import React, { useEffect, useMemo, useState } from "react";
import styles from "./EditWorkflowDialog.module.scss";

const { AssetTypeSelection } = Assets.view;

const cx = classNames.bind(styles);

const SelectionType = {
  all: "all",
  list: "list",
  filter: "filter",
} as const;

export type SelectionTypeType =
  (typeof SelectionType)[keyof typeof SelectionType];

type FormValues = {
  templateName: string;
  templateDescription?: string;
  workflowName?: string;
  workflowDescription?: string;
  workflowCategory?: string;
  workflowOwner?: string;
  assetType: AssetTypeType;
  selectionType: SelectionTypeType;
  assetFilter: Record<string, any>;
  assetList: string[];
  dismissalReasons: string[];
};

const OverviewStep = ({ template }: { template: WorkflowTemplateType }) => {
  const { currentStep, updateStep } = useMultiStepFormContext();
  const STEP_NUMBER = 1;
  const isStepActive = currentStep === STEP_NUMBER;
  const form = useForm<FormValues>({
    defaultValues: {
      templateName: template.name,
      templateDescription: template.description,
      workflowName: template.config.workflow_name,
      workflowDescription: template.config.workflow_description,
      workflowCategory: template.category_id,
      workflowOwner: template.workflow_owner_id,
      dismissalReasons: template.dismissal_reasons ?? [],
    },
  });
  const getFormState = useMemoizedFormState(form);

  useEffect(() => {
    updateStep(STEP_NUMBER, {
      getValues: () => form.getValues() as any,
      getFormState,
    });
  }, [getFormState]);

  return isStepActive ? (
    <Form {...form}>
      <TextInput
        label="Template Name"
        name="templateName"
        isFluid
        isRequired
      />
      <TextAreaInput
        label="Template Description"
        name="templateDescription"
        isFluid
      />
      <TextInput
        label="Workflow Name"
        name="workflowName"
        isFluid
      />
      <TextAreaInput
        label="Workflow Description"
        name="workflowDescription"
        isFluid
      />
      <EntityDropdownInput
        fetchEntityList={WorkflowCategoriesAdapter.getList}
        label="Workflow Category"
        name="workflowCategory"
        isMulti={false}
        sort={{
          sortBy: "name",
          sortDirection: "asc",
        }}
      />
      <EntityDropdownInput
        fetchEntityList={UsersAdapter.getList}
        label="Workflow Owner"
        name="workflowOwner"
        isMulti={false}
        sort={{
          sortBy: "name",
          sortDirection: "asc",
        }}
      />
      <ListInput
        name="dismissalReasons"
        label="Dismissal Reasons"
        description="Optional list of allowed reasons for dismissing a workflow. Type a reason and press Enter to add it."
        placeholder="Type a reason and press Enter..."
        isFluid
      />
    </Form>
  ) : null;
};

const toParentFields = (
  fields: FilterBuilderFieldsType,
  child: AssetTypeType
) => {
  const converted: FilterBuilderFieldsType = {};
  for (const [key, value] of Object.entries(fields)) {
    converted[`${child}.${key}`] = {
      ...value,
      display_name: `${startCase(AssetType.FACILITY)} ${value.display_name}`,
    };
  }
  return converted;
};

const toAttributeFields = (
  data: CustomAttributeType[] | undefined,
  assetType: AssetTypeType
) => {
  const customAttributes: FilterBuilderFieldsType = {};
  if (data) {
    for (const attribute of data) {
      try {
        customAttributes[
          `${assetType}.custom_attributes.${attribute.field_name}`
        ] = {
          data_type: toFieldType(attribute.data_type),
          display_name: attribute.display_name,
          ...(attribute.data_type === AttributeDataType.PICK_LIST &&
            ({
              pick_list_options: attribute.pick_list_values,
            } as any)),
        };
      } catch (err) {
        // don't add unsupported data types
      }
    }
  }
  return customAttributes;
};

const toFieldType = (
  dataType: AttributeDataTypeType
): FilterBuilderSupportedDataTypesType => {
  switch (dataType) {
    case AttributeDataType.NUMBER:
    case AttributeDataType.STRING:
    case AttributeDataType.BOOLEAN:
    case AttributeDataType.PICK_LIST:
      return dataType;
    case AttributeDataType.INTEGER:
      return AttributeDataType.NUMBER;
    default:
      throw new Error(`Unsupported attribute type ${dataType}`);
  }
};

const dateField = (
  type: "Created" | "Updated"
): FilterBuilderFieldDefinition => {
  return {
    data_type: "date",
    format: "yyyy-MM-dd",
    display_name: `${type} At`,
  };
};

const useFilterBuilderAssetFields = (
  assetType: AssetTypeType
): FilterBuilderFieldsType => {
  const { data: facilityAttributeData } = useListCustomAttributes({
    filters: { entity_type: "facility" },
  });
  const { data: attributeData } = useListCustomAttributes({
    filters: { entity_type: assetType },
  });

  const { data: equipmentTypeData } = useListEquipmentTypes({});
  const { data: deviceTypeData } = useListDeviceTypes(undefined);
  const { data: measurementTypeData } = useListMeasurementTypes();
  const { data: flowTypeData } = useListFlowTypes();
  const { data: flowProductTypeData } = useListFlowProductTypes();
  const { data: flowProductCategoryData } = useListFlowProductCategories();

  return useMemo(() => {
    const customAttributes = toAttributeFields(attributeData?.data, assetType);
    const facilityCustomAttributes = toAttributeFields(
      facilityAttributeData?.data,
      AssetType.FACILITY
    );

    if (assetType === AssetType.DEVICE && deviceTypeData) {
      for (const type of deviceTypeData.data) {
        for (const attribute of type.attributes) {
          try {
            customAttributes[`device.attributes.${attribute.id}`] = {
              data_type: toFieldType(attribute.data_type),
              display_name: attribute.name,
            } as any;
          } catch (err) {
            // don't add unsupported data types
          }
        }
      }
    }

    const facilityFields: FilterBuilderFieldsType = {
      "facility.id": { data_type: "string", display_name: "ID" },
      "facility.name": { data_type: "string", display_name: "Name" },
      "facility.status": {
        data_type: "pick-list",
        display_name: "Status",
        pick_list_options: Object.values(FacilityStatus),
      },
      "facility.updated_at": dateField("Updated"),
      "facility.created_at": dateField("Created"),
    };
    const facilityFieldsWithAttributes: FilterBuilderFieldsType = {
      ...facilityFields,
      ...facilityCustomAttributes,
    };

    const equipmentFields: FilterBuilderFieldsType = {
      "equipment.id": { data_type: "string", display_name: "ID" },
      "equipment.name": { data_type: "string", display_name: "Name" },
      "equipment.status": {
        data_type: "pick-list",
        display_name: "Status",
        pick_list_options: Object.values(EquipmentStatus),
      },
      "equipment.type.id": {
        data_type: "pick-list",
        display_name: "Type ID",
        pick_list_options: equipmentTypeData
          ? equipmentTypeData.data.map((v) => v.id)
          : [],
      },
      "equipment.updated_at": dateField("Updated"),
      "equipment.created_at": dateField("Created"),
      ...toParentFields(facilityFieldsWithAttributes, AssetType.EQUIPMENT),
    };

    const deviceFields: FilterBuilderFieldsType = {
      "device.id": { data_type: "string", display_name: "ID" },
      "device.name": { data_type: "string", display_name: "Name" },
      "device.status": {
        data_type: "pick-list",
        display_name: "Status",
        pick_list_options: Object.values(DeviceStatus),
      },
      "device.type_id": {
        data_type: "pick-list",
        display_name: "Type ID",
        pick_list_options: deviceTypeData
          ? deviceTypeData.data.map((v) => v.id)
          : [],
      },
      "device.measurement_types": {
        data_type: "pick-list",
        display_name: "Measurement Type",
        pick_list_options: measurementTypeData
          ? measurementTypeData.map((v) => v.id)
          : [],
      },
      "device.updated_at": dateField("Updated"),
      "device.created_at": dateField("Created"),
    };

    const flowFields: FilterBuilderFieldsType = {
      "flow.id": { data_type: "string", display_name: "ID" },
      "flow.name": { data_type: "string", display_name: "Name" },
      "flow.status": {
        data_type: "pick-list",
        display_name: "Status",
        pick_list_options: Object.values(FlowStatus),
      },
      "flow.type": {
        data_type: "pick-list",
        display_name: "Type",
        pick_list_options: flowTypeData
          ? flowTypeData.data.map((v) => v.id)
          : [],
      },
      "flow.product_type": {
        data_type: "pick-list",
        display_name: "Product Type",
        pick_list_options: flowProductTypeData
          ? flowProductTypeData.data.map((v) => v.id)
          : [],
      },
      "flow.product_category": {
        data_type: "pick-list",
        display_name: "Product Category",
        pick_list_options: flowProductCategoryData
          ? flowProductCategoryData.data.map((v) => v.id)
          : [],
      },
      "flow.updated_at": dateField("Updated"),
      "flow.created_at": dateField("Created"),
      ...toParentFields(facilityFieldsWithAttributes, AssetType.FLOW),
    };

    return {
      ...customAttributes,
      ...(assetType === AssetType.FACILITY && facilityFields),
      ...(assetType === AssetType.EQUIPMENT && equipmentFields),
      ...(assetType === AssetType.DEVICE && deviceFields),
      ...(assetType === AssetType.FLOW && flowFields),
    };
  }, [
    assetType,
    facilityAttributeData,
    attributeData,
    equipmentTypeData,
    deviceTypeData,
    measurementTypeData,
    flowTypeData,
    flowProductTypeData,
    flowProductCategoryData,
  ]);
};

const AssetStep = ({ template }: { template: WorkflowTemplateType }) => {
  const assetTypeOptions = useGetAssetTypeOptions().filter(
    (v) =>
      v.value === AssetTypeSelection.DEVICE ||
      v.value === AssetTypeSelection.EQUIPMENT ||
      v.value === AssetTypeSelection.FACILITY ||
      v.value === AssetTypeSelection.FLOW
  );
  const { currentStep, updateStep } = useMultiStepFormContext();
  const STEP_NUMBER = 2;
  const isStepActive = currentStep === STEP_NUMBER;
  const [assetFilter, setAssetFilter] = useState(template.asset_filter);

  const form = useForm<FormValues>({
    defaultValues: {
      assetType: template.asset_type,
      selectionType: SelectionType.filter,
      assetFilter: template.asset_filter,
    },
  });
  const getFormState = useMemoizedFormState(form);
  form.setValue("assetFilter", assetFilter);

  const [selectionType, assetType] = form.watch(["selectionType", "assetType"]);

  useEffect(() => {
    updateStep(STEP_NUMBER, {
      getValues: () => form.getValues() as any,
      getFormState,
    });
  }, [getFormState]);

  useEffect(() => {
    if (assetType !== template.asset_type)
      setAssetFilter({ asset_type: assetType });
    else setAssetFilter(template.asset_filter);
  }, [assetType, template.asset_type, template.asset_filter]);

  const fields = useFilterBuilderAssetFields(assetType);

  return isStepActive ? (
    <Form {...form}>
      <p>Assign for which assets the template will generate workflows.</p>
      <RadioInput
        label="Associated Asset Type"
        name="assetType"
        isRequired
        options={assetTypeOptions}
        valueKey="value"
        labelKey="label"
      />
      <RadioInput
        label="Asset Selection Type"
        name="selectionType"
        labelKey="label"
        valueKey="value"
        options={[
          { label: "Add to all assets", value: SelectionType.all },
          { label: "Define specific assets", value: SelectionType.list },
          { label: "Filter assets", value: SelectionType.filter },
        ]}
        isRequired
      />
      {selectionType === SelectionType.list ? (
        <AssetDropdownInput
          assetType={assetType}
          name="assetList"
        />
      ) : null}
      {selectionType === SelectionType.filter ? (
        <FilterBuilder
          title="Filter Definition"
          fields={fields}
          filter={assetFilter}
          setFilter={setAssetFilter}
        />
      ) : null}
    </Form>
  ) : null;
};

const EditWorkflowContent = ({ isOpen, onClose, template }) => {
  const { stepper, footer, currentStep, goToStep } = useMultiStepFormContext();
  useEffect(() => {
    if (isOpen) goToStep(1);
  }, [isOpen]);
  return (
    <Dialog
      title={currentStep === 1 ? "Overview" : "Assets"}
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className={cx("container")}>
        <div className={cx("stepper-container")}>{stepper}</div>
        <div className={cx("form-container")}>
          <OverviewStep template={template} />
          <AssetStep template={template} />
          {footer}
        </div>
      </div>
    </Dialog>
  );
};

const patchAssetFilterFromFormValues = (update: FormValues) => {
  switch (update.selectionType) {
    case SelectionType.all:
    default:
      return { asset_type: update.assetType };
    case SelectionType.list:
      return {
        asset_type: update.assetType,
        ...(update.assetList.length && {
          [`${update.assetType}.id`]: { $in: update.assetList },
        }),
      };
    case SelectionType.filter:
      return {
        ...update.assetFilter,
        asset_type: update.assetType,
      };
  }
};

export const EditWorkflowDialog = ({
  onClose,
  template,
  isOpen,
}: Pick<DialogProps, "onClose"> & {
  template: WorkflowTemplateType;
  isOpen: boolean;
}) => {
  const updateTemplateMutation = useUpdateWorkflowTemplateByApiVersion(
    template.api_version
  );

  const updateTemplate = (values: FormValues[]) => {
    const update = values.reduce(
      (total, current) => ({ ...total, ...current }),
      {} as FormValues
    );

    updateTemplateMutation.mutate(
      {
        id: template.id,
        data: {
          name: update.templateName,
          description: update.templateDescription,
          category_id: update.workflowCategory,
          workflow_owner_id: update.workflowOwner,
          asset_type: update.assetType,
          config: {
            ...template.config,
            workflow_name: update.workflowName,
            workflow_description: update.workflowDescription,
          },
          asset_filter: patchAssetFilterFromFormValues(update),
          dismissal_reasons: update.dismissalReasons ?? [],
        },
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  return (
    <MultiStepFormProvider
      steps={[
        {
          label: "Overview",
        },
        {
          label: "Assets",
        },
      ]}
      orientation="vertical"
      onCancel={onClose}
      onSubmit={updateTemplate as any}
      submissionActionText="Update"
      stepperClassName={cx("sticky-stepper")}
    >
      <EditWorkflowContent
        isOpen={isOpen}
        onClose={onClose}
        template={template}
      />
    </MultiStepFormProvider>
  );
};
